import { BrandFormData, ExtendedBrand, validateForm } from './BrandTypes';
import { showToast } from '../../utils/toast';
import {
  createBrand,
  updateBrand,
  deleteBrand as deleteBrandAPI,
  uploadBrandDocument
} from '../../services/brand';
import { getChangedFields } from '../../utils/formUtils';
import { is } from 'date-fns/locale';



const emptyToNull = (value?: string) =>
  value && value.trim() !== "" ? value : null;


// Handler for input changes
export const handleChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  editData: BrandFormData,
  setEditData: React.Dispatch<React.SetStateAction<BrandFormData | null>>,
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  const { name, value, type } = e.target;
  const newValue =
    type === "checkbox"
      ? (e.target as HTMLInputElement).checked
      : value;

  // Clear error for this field when user edits it
  if (errors[name]) {
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  }

  // ✅ SAFE functional state update
  if (name === "name") {
    setEditData((prev) => ({
      ...prev!,
      name: value,
      groupId: value,
    }));
  } else {
    setEditData((prev) => ({
      ...prev!,
      [name]: newValue,
    }));
  }
};


// Page change handler
export const handlePageChange = (
  setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  newPage: number
) => {
  // Reset data first
  setData([]);
  // Show loading state
  setIsLoading(true);
  // Set the new page (this will trigger the useEffect that fetches data with the new page)
  console.log(`Changing to page: ${newPage}`);
  setPage(newPage);
};

// Page size change handler
export const handlePageSizeChange = (
  setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setPageSize: React.Dispatch<React.SetStateAction<number>>,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  newSize: number
) => {
  setData([]);
  setIsLoading(true);
  setPageSize(newSize);
  setPage(1); // Reset to first page when changing page size
};

// Search handler
export const handleSearchChange = (
  setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  setPage: React.Dispatch<React.SetStateAction<number>>,
  currentQuery: string,
  newQuery: string
) => {
  if (currentQuery !== newQuery) {
    setData([]);
    setIsLoading(true);
    setSearchQuery(newQuery);
    setPage(1); // Reset to first page when searching
  }
};

// Sort handler
export const handleSortChange = (
  setData: React.Dispatch<React.SetStateAction<ExtendedBrand[]>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setSortBy: React.Dispatch<React.SetStateAction<string>>,
  setSortOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>,
  currentSortBy: string,
  currentSortOrder: 'asc' | 'desc',
  columnId: string,
  order: 'asc' | 'desc'
) => {
  if (currentSortBy !== columnId || currentSortOrder !== order) {
    setData([]);
    setIsLoading(true);
    setSortBy(columnId);
    setSortOrder(order);
  }
};

// Row click handler
export const handleRowClick = (
  Brand: ExtendedBrand,
  setSelectedBrand: React.Dispatch<React.SetStateAction<ExtendedBrand | null>>,
  setEditData: React.Dispatch<React.SetStateAction<BrandFormData | null>>,
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>,
  setDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>
) => {
  setSelectedBrand(Brand);

  // Extract form data from the Brand for editing
  const formData: BrandFormData = {
    id: Brand._id,
    name: Brand.name,
    groupId: Brand.groupId || '',
    contactNo: Brand.mobileNo,
    contactEmail: Brand.email,
    isEv: Brand.isEv || false,
    isPublic: Brand.isPublic || false,
    category: Brand.category || [],
    subCategory: Brand.subCategory || [],
    schemeBucket: Brand.schemeBucket || [],
    dashboard: Brand.dashboard || false,
    suspended: Brand.suspended || false,
    isBrand: Brand.isBrand || false,
    publicFor: Brand.publicFor || [],
    lender: Brand.lender || '',
    kycGstNo: Brand.kycGstNo || '',
    kycBank: Brand.kycBank,
    kycShop: Brand.kycShop,
    kycDocs: Brand.kycDocs ? {
      shopFront: Brand.kycDocs.shopFront || null,
      shopInner: Brand.kycDocs.shopInner || null,
      evCer: Brand.kycDocs.evCer || null
    } : undefined,
    kyc: Brand.kyc
  };

  setEditData(formData);
  setIsEditing(false);
  setDetailsOpen(true);
};

// Save handler for Brand updates
export const handleSave = async (
  validateFormFn: () => boolean,
  editData: BrandFormData | null,
  selectedBrand: ExtendedBrand | null,
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>,
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedBrand: React.Dispatch<React.SetStateAction<ExtendedBrand | null>>,
  fetchBrands: () => Promise<void>,
  getToken: () => string | null
) => {
  if (!validateFormFn() || !editData || !selectedBrand) {
    return;
  }

  setIsUpdating(true);

  try {
    const token = getToken();

    const formattedEditData = {
      name: editData.name,
      groupId: editData.groupId,
      mobileNo: emptyToNull(editData.contactNo),
      email: emptyToNull(editData.contactEmail),
      isEv: editData.isEv,
      lender: editData.lender,
      publicFor: editData.publicFor,
      isPublic: editData.isPublic,
      category: editData.category,
      subCategory: editData.subCategory,
      schemeBucket: editData.schemeBucket,
      dashboard: editData.dashboard,
      suspended: editData.suspended,
      isBrand: editData.isBrand
    };

    // Keep originalData exactly as in the brand (no emptyToNull)
    const originalData = {
      name: selectedBrand.name,
      groupId: selectedBrand.groupId,
      mobileNo: emptyToNull(selectedBrand.mobileNo), // normalize here too
      email: emptyToNull(selectedBrand.email),
      isEv: selectedBrand.isEv,
      lender: selectedBrand.lender,
      publicFor: selectedBrand.publicFor,
      isPublic: selectedBrand.isPublic,
    };

    console.log("==== DEBUG: Original Brand Data ====");
    console.log(originalData);

    console.log("==== DEBUG: Formatted Edited Data ====");
    console.log(formattedEditData);

    const updatedData = getChangedFields(originalData, formattedEditData, true); // pass true for logging

    console.log("==== DEBUG: Only Sending Changed Fields ====");
    console.log(updatedData);

    if (Object.keys(updatedData).length === 0) {
      showToast.info('No changes detected');
      setIsUpdating(false);
      setIsEditing(false);
      return;
    }

    const updatedBrand = await updateBrand(
      selectedBrand._id,
      updatedData,
      token
    );

    setSelectedBrand({
      ...selectedBrand,
      ...updatedBrand
    });

    setIsEditing(false);
    fetchBrands();
    showToast.success('Updated successfully');
  } catch (error) {
    console.error("[Component] Error updating Brand:", error);
    showToast.error("Failed to update");
  } finally {
    setIsUpdating(false);
  }
};

// Bank KYC edit handlers
export const handleEditBankKyc = (
  Brand: ExtendedBrand,
  setBankKycData: React.Dispatch<React.SetStateAction<{ accountNo: string; holderName: string; ifsc: string } | null>>,
  setIsBankKycEditing: React.Dispatch<React.SetStateAction<boolean>>
) => {
  // Initialize bank KYC data from Brand or with empty values
  setBankKycData({
    accountNo: Brand.kycBank?.accountNo || '',
    holderName: Brand.kycBank?.holderName || '',
    ifsc: Brand.kycBank?.ifsc || ''
  });

  setIsBankKycEditing(true);
};

export const handleBankKycChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  bankKycData: { accountNo: string; holderName: string; ifsc: string } | null,
  setBankKycData: React.Dispatch<React.SetStateAction<{ accountNo: string; holderName: string; ifsc: string } | null>>,
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  if (!bankKycData) return;

  const { name, value } = e.target;

  // Clear error for this field
  if (errors[`bank_${name}`]) {
    setErrors({
      ...errors,
      [`bank_${name}`]: ''
    });
  }

  setBankKycData({
    ...bankKycData,
    [name]: value
  });
};

export const saveBankKyc = async (
  selectedBrand: ExtendedBrand | null,
  bankKycData: { accountNo: string; holderName: string; ifsc: string } | null,
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedBrand: React.Dispatch<React.SetStateAction<ExtendedBrand | null>>,
  setIsBankKycEditing: React.Dispatch<React.SetStateAction<boolean>>,
  fetchBrands: () => Promise<void>,
  getToken: () => string | null
) => {
  if (!selectedBrand || !bankKycData) return;

  // Validate bank KYC data
  const newErrors: Record<string, string> = {};
  let isValid = true;

  if (!bankKycData.accountNo) {
    newErrors.accountNo = 'Account number is required';
    isValid = false;
  }

  if (!bankKycData.holderName) {
    newErrors.holderName = 'Account holder name is required';
    isValid = false;
  }

  if (!bankKycData.ifsc) {
    newErrors.ifsc = 'IFSC code is required';
    isValid = false;
  }

  if (!isValid) {
    setErrors({
      ...errors,
      ...newErrors
    });
    return;
  }

  setIsUpdating(true);

  try {
    const token = getToken();

    // Format bank data for API
    const updateData = {
      kycBank: {
        accountNo: bankKycData.accountNo,
        holderName: bankKycData.holderName,
        ifsc: bankKycData.ifsc
      },
      kyc: {
        ...selectedBrand.kyc,
        bank: true
      }
    };

    // Call the API to update the Brand using mobileNo instead of _id
    const updatedBrand = await updateBrand(
      selectedBrand._id,  // Using mobileNo instead of _id
      updateData,
      token
    );

    // Update the selected Brand in state with the new data
    setSelectedBrand({
      ...selectedBrand,
      ...updatedBrand
    });

    setIsBankKycEditing(false);
    fetchBrands();
    showToast.success('Bank details updated successfully');
  } catch (error) {
    console.error("[Component] Error updating bank KYC:", error);
    showToast.error("Failed to update bank information");
  } finally {
    setIsUpdating(false);
  }
};

// Shop KYC edit handlers
export const handleEditShopKyc = (
  Brand: ExtendedBrand,
  setShopKycData: React.Dispatch<React.SetStateAction<{ name: string; address: string; state: string; city: string; pin: string; kycGstNo?: string } | null>>,
  setIsShopKycEditing: React.Dispatch<React.SetStateAction<boolean>>
) => {
  // Initialize shop KYC data from Brand or with empty values
  setShopKycData({
    name: Brand.kycShop?.name || '',
    address: Brand.kycShop?.address || '',
    state: Brand.kycShop?.state || '',
    city: Brand.kycShop?.city || '',
    pin: Brand.kycShop?.pin || '',
    kycGstNo: Brand.kycGstNo || ''
  });

  setIsShopKycEditing(true);
};

export const handleShopKycChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  shopKycData: { name: string; address: string; state: string; city: string; pin: string; kycGstNo?: string } | null,
  setShopKycData: React.Dispatch<React.SetStateAction<{ name: string; address: string; state: string; city: string; pin: string; kycGstNo?: string } | null>>,
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
) => {
  if (!shopKycData) return;

  const { name, value } = e.target;

  // Clear error for this field
  if (errors[`shop_${name}`]) {
    setErrors({
      ...errors,
      [`shop_${name}`]: ''
    });
  }

  setShopKycData({
    ...shopKycData,
    [name]: value
  });
};

export const saveShopKyc = async (
  selectedBrand: ExtendedBrand | null,
  shopKycData: { name: string; address: string; state: string; city: string; pin: string; kycGstNo?: string } | null,
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedBrand: React.Dispatch<React.SetStateAction<ExtendedBrand | null>>,
  setIsShopKycEditing: React.Dispatch<React.SetStateAction<boolean>>,
  fetchBrands: () => Promise<void>,
  getToken: () => string | null
) => {
  if (!selectedBrand || !shopKycData) return;

  // Validate shop KYC data
  const newErrors: Record<string, string> = {};
  let isValid = true;

  if (!shopKycData.name) {
    newErrors.shop_name = 'Shop name is required';
    isValid = false;
  }

  if (!shopKycData.address) {
    newErrors.shop_address = 'Address is required';
    isValid = false;
  }

  if (!shopKycData.state) {
    newErrors.shop_state = 'State is required';
    isValid = false;
  }

  if (!shopKycData.city) {
    newErrors.shop_city = 'City is required';
    isValid = false;
  }

  if (!shopKycData.pin) {
    newErrors.shop_pin = 'PIN code is required';
    isValid = false;
  } else if (!/^\d{6}$/.test(shopKycData.pin)) {
    newErrors.shop_pin = 'PIN code must be 6 digits';
    isValid = false;
  }

  if (!isValid) {
    setErrors({
      ...errors,
      ...newErrors
    });
    return;
  }

  setIsUpdating(true);

  try {
    const token = getToken();

    // Format shop data for API
    const updateData = {
      kycShop: {
        name: shopKycData.name,
        address: shopKycData.address,
        state: shopKycData.state,
        city: shopKycData.city,
        pin: shopKycData.pin
      },
      kycGstNo: shopKycData.kycGstNo || '',
      kyc: {
        ...selectedBrand.kyc,
        shop: true
      }
    };

    // Call the API to update the Brand using mobileNo instead of _id
    const updatedBrand = await updateBrand(
      selectedBrand._id,  // Using mobileNo instead of _id
      updateData,
      token
    );

    // Update the selected Brand in state with the new data
    setSelectedBrand({
      ...selectedBrand,
      ...updatedBrand
    });

    setIsShopKycEditing(false);
    fetchBrands();
    showToast.success('Shop details updated successfully');
  } catch (error) {
    console.error("[Component] Error updating shop KYC:", error);
    showToast.error("Failed to update shop information");
  } finally {
    setIsUpdating(false);
  }
};

// Document KYC handlers
export const handleEditDocsKyc = (
  setDocsKycFiles: React.Dispatch<React.SetStateAction<{ shopFront: File | null; shopInner: File | null; evCer: File | null }>>,
  setIsDocsKycEditing: React.Dispatch<React.SetStateAction<boolean>>
) => {
  // Reset files when entering edit mode
  setDocsKycFiles({
    shopFront: null,
    shopInner: null,
    evCer: null
  });

  setIsDocsKycEditing(true);
};

export const handleFileChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  docType: 'shopFront' | 'shopInner' | 'evCer',
  setDocsKycFiles: React.Dispatch<React.SetStateAction<{ shopFront: File | null; shopInner: File | null; evCer: File | null }>>
) => {
  if (e.target.files && e.target.files.length > 0) {
    const file = e.target.files[0];

    setDocsKycFiles(prev => ({
      ...prev,
      [docType]: file
    }));
  }
};

export const saveDocsKyc = async (
  selectedBrand: ExtendedBrand | null,
  docsKycFiles: { shopFront: File | null; shopInner: File | null; evCer: File | null },
  errors: Record<string, string>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setIsUpdating: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedBrand: React.Dispatch<React.SetStateAction<ExtendedBrand | null>>,
  setIsDocsKycEditing: React.Dispatch<React.SetStateAction<boolean>>,
  fetchBrands: () => Promise<void>,
  getToken: () => string | null
) => {
  if (!selectedBrand) return;

  // Check if at least one file is selected
  if (!docsKycFiles.shopFront && !docsKycFiles.shopInner && !docsKycFiles.evCer) {
    setErrors({
      ...errors,
      docs: 'Please upload at least one document'
    });
    return;
  }

  setIsUpdating(true);

  try {
    const token = getToken();
    const mobileNo = selectedBrand.mobileNo;
    const uploadPromises: Promise<any>[] = [];

    // Upload Shop Front image if exists
    if (docsKycFiles.shopFront) {
      uploadPromises.push(uploadBrandDocument('Brand', 'shopFront', mobileNo, docsKycFiles.shopFront, token || ''));
    }

    // Upload Shop Inner image if exists
    if (docsKycFiles.shopInner) {
      uploadPromises.push(uploadBrandDocument('Brand', 'shopInner', mobileNo, docsKycFiles.shopInner, token || ''));
    }

    // Upload EV Certificate if exists and Brand is EV
    if (docsKycFiles.evCer && selectedBrand.isEv) {
      uploadPromises.push(uploadBrandDocument('Brand', 'evCer', mobileNo, docsKycFiles.evCer, token || ''));
    }

    // Wait for all uploads to complete
    if (uploadPromises.length > 0) {
      await Promise.all(uploadPromises);
      showToast.success('Documents uploaded successfully');
    }

    setIsDocsKycEditing(false);
    fetchBrands();
  } catch (error) {
    console.error("[Component] Error uploading documents:", error);
    showToast.error("Failed to upload documents");
  } finally {
    setIsUpdating(false);
  }
};

// Delete Brand handler
export const handleDeleteBrand = async (
  selectedBrand: ExtendedBrand | null,
  setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>,
  setDeleteConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>,
  fetchBrands: () => Promise<void>,
  getToken: () => string | null
) => {
  if (!selectedBrand) return;

  setIsDeleting(true);

  try {
    const token = getToken();

    // Call the API to delete the Brand using name
    await deleteBrandAPI(selectedBrand.name, token);

    setDeleteConfirmOpen(false);
    setDetailsOpen(false);
    showToast.success('Deleted successfully');
    fetchBrands();
  } catch (error) {
    console.error("[Component] Error deleting Brand:", error);
    showToast.error("Failed to delete");
  } finally {
    setIsDeleting(false);
  }
};

// Create Brand handler
export const handleCreateBrand = async (
  formData: any,
  setAddBrandOpen: React.Dispatch<React.SetStateAction<boolean>>,
  fetchBrands: () => Promise<void>,
  getToken: () => string | null
) => {
  try {
    const token = getToken();

    // Prepare Brand data for API
    const BrandData = {
      name: formData.name,
      groupId: formData.groupId || formData.name,
      mobileNo: formData.contactNo,
      email: formData.contactEmail,
      isEv: formData.isEv || false,
      isPublic: formData.isPublic || false,
      category: formData.category || [],
      subCategory: formData.subCategory || [],
      schemeBucket: formData.schemeBucket || [],
      dashboard: formData.dashboard || false,
      suspended: formData.suspended || false,
      isBrand: formData.isBrand || false,
      publicFor: formData.publicFor,
      lender: formData.lender || undefined,
      kycGstNo: formData.kycGstNo || '',
      kyc: {
        bank: formData.kyc?.bank ? {
          accountNo: formData.kyc.bank.accountNo || '',
          holderName: formData.kyc.bank.holderName || '',
          ifsc: formData.kyc.bank.ifsc || ''
        } : undefined,
        shop: formData.kyc?.shop ? {
          name: formData.kyc.shop.name || '',
          address: formData.kyc.shop.address || '',
          state: formData.kyc.shop.state || '',
          city: formData.kyc.shop.city || '',
          pin: formData.kyc.shop.pin || ''
        } : undefined,
        docs: formData.kyc?.docs ? {
          shopFront: formData.kyc.docs.shopFront || null,
          shopInner: formData.kyc.docs.shopInner || null,
          evCer: formData.isEv ? formData.kyc.docs.evCer || null : null
        } : undefined
      }
    };

    // Extract document files if provided
    let docFiles;
    if (formData.kyc?.docs) {
      docFiles = {
        shopFront: formData.kyc.docs.shopFront || null,
        shopInner: formData.kyc.docs.shopInner || null,
        evCer: formData.isEv ? formData.kyc.docs.evCer || null : null
      };
    }

    // Call the API to create the Brand
    console.log('Creating Brand with data:', BrandData);
    await createBrand(BrandData, token, docFiles);

    setAddBrandOpen(false);
    fetchBrands();
  } catch (error) {
    console.error("[Component] Error creating Brand:", error);
    showToast.error("Failed to create");
  }
}; 